import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Shield, FileCode, Brain, ChevronDown, GitBranch, Check, Package, FolderGit2 } from 'lucide-react';
import toast from '../../design-system/toast';
import { ApplicationResponse, ApplicationRepository } from '../../types/application';
import { ScanType, ScanCreateRequest } from '../../types/scan';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints, repoBranchesEndpoint } from '../../utils/api';
import { getRepositoryKey } from '../../utils/repository';
import { credentialsInvalidMessage, isFormProvider, providerLabel } from '../../config/providers';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useQuotaBalance } from '../../hooks/useQuotaBalance';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { Spinner } from '../../design-system';
import QuotaExceededModal, { QuotaType } from '../common/QuotaExceededModal';
import PopoverPortal from '../common/PopoverPortal';
import PostHogService, { FeatureDepthEvent } from '../../posthog';
import { isUnlimitedQuota } from '../../utils/quota';

interface CreateScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (batchId: string) => void;
  preselectedApplicationId?: string;
}

const SCAN_TYPES: Array<{
  id: ScanType;
  name: string;
  description: string;
  icon: React.ReactNode;
  duration: string;
  recommended?: boolean;
}> = [
  {
    id: 'deep_ai_scan',
    name: 'Deep AI Security Scan',
    description: 'Comprehensive cross-file security analysis using chunked AI scanning',
    icon: <Brain className="w-6 h-6" />,
    duration: '~5-15 min per repo',
    recommended: true,
  },
  {
    id: 'semgrep_scan',
    name: 'Security Scan',
    description: 'Identify security vulnerabilities and code quality issues using Semgrep',
    icon: <FileCode className="w-6 h-6" />,
    duration: '~5-10 min per repo',
  },
  {
    id: 'secrets_scan',
    name: 'Secrets Scan',
    description: 'Detect exposed secrets, API keys, and credentials in your codebase',
    icon: <Shield className="w-6 h-6" />,
    duration: '~2-5 min per repo',
  },
  {
    id: 'sbom_scan',
    name: 'SBOM Scan',
    description: 'Generate a CycloneDX SBOM, match dependencies against CVE feeds, and evaluate license policy',
    icon: <Package className="w-6 h-6" />,
    duration: '~3-8 min per repo',
  },
];

interface BranchEntry {
  name: string;
  commit_sha?: string;
  updated_at?: string | null;
}

interface ScanBranchSelectorProps {
  organizationId: string;
  repo: ApplicationRepository;
  value: string;
  onChange: (branch: string) => void;
  disabled?: boolean;
  /** Render an inline trigger (for use inside the context chip) instead of a full-width input. */
  compact?: boolean;
}

const ScanBranchSelector: React.FC<ScanBranchSelectorProps> = ({
  organizationId,
  repo,
  value,
  onChange,
  disabled = false,
  compact = false,
}) => {
  const api = useAuthenticatedApi();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [branches, setBranches] = useState<BranchEntry[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const provider = repo.provider || 'github';

  const fetchBranches = useCallback(async (force = false) => {
    if (!force && branches.length > 0) return;

    setIsLoadingBranches(true);
    setLoadError(null);
    try {
      const endpoint = repoBranchesEndpoint(organizationId, provider, repo.full_name, repo.gitlab_instance_url);
      const data = await api.get<BranchEntry[]>(endpoint);
      setBranches(data);
    } catch (err: any) {
      console.error('Failed to load branches:', err);
      if (isFormProvider(provider) && err?.status === 401) {
        setLoadError(credentialsInvalidMessage(provider));
      } else {
        setLoadError(err?.message || 'Failed to load branches');
      }
    } finally {
      setIsLoadingBranches(false);
    }
  }, [api, branches.length, organizationId, provider, repo.full_name, repo.gitlab_instance_url]);

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearch('');
    fetchBranches();
  };

  const filteredBranches = search
    ? branches.filter((branch) => branch.name.toLowerCase().includes(search.toLowerCase()))
    : branches;

  return (
    <div ref={containerRef} className="relative">
      {compact ? (
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 max-w-full font-mono text-sm text-ui-text-primary hover:text-brand-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          title="Change branch"
        >
          <GitBranch className="w-3.5 h-3.5 text-ui-text-tertiary shrink-0" />
          <span className="truncate max-w-[10rem]">{value || 'Select branch...'}</span>
          <ChevronDown className="w-3.5 h-3.5 text-ui-text-tertiary shrink-0" />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-ui-background-primary border border-ui-border-secondary rounded-lg text-sm text-ui-text-primary hover:border-ui-border-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="min-w-0 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-ui-text-tertiary shrink-0" />
            <span className="truncate">{value || 'Select branch...'}</span>
          </span>
          <ChevronDown className="w-4 h-4 text-ui-text-tertiary shrink-0" />
        </button>
      )}

      <PopoverPortal
        anchorRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        align={compact ? 'start' : 'end'}
        {...(compact ? { width: 288 } : { matchAnchorWidth: true })}
        className={`bg-ui-background-primary border border-ui-border-secondary rounded-lg shadow-lg overflow-hidden ${
          compact ? '' : 'min-w-72'
        }`}
      >
          <div className="p-2 border-b border-ui-border-primary">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter branches..."
              autoFocus
              className="w-full px-3 py-2 text-sm bg-ui-background-tertiary border border-ui-border-secondary rounded-lg text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
            />
          </div>

          <div className="max-h-56 overflow-y-auto">
            {isLoadingBranches ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size={16} />
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-red-500">{loadError}</p>
                <button
                  type="button"
                  onClick={() => fetchBranches(true)}
                  className="mt-2 text-xs text-brand-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : filteredBranches.length === 0 ? (
              <p className="text-xs text-ui-text-tertiary text-center py-4">
                {search ? 'No matching branches' : 'No branches found'}
              </p>
            ) : (
              filteredBranches.map((branch) => {
                const isActive = branch.name === value;
                return (
                  <button
                    key={branch.name}
                    type="button"
                    onClick={() => {
                      onChange(branch.name);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 hover:bg-ui-background-tertiary transition-colors ${
                      isActive
                        ? 'text-brand-primary font-medium'
                        : 'text-ui-text-primary'
                    }`}
                  >
                    <span className="truncate">{branch.name}</span>
                    {isActive && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
      </PopoverPortal>
    </div>
  );
};

const CreateScanModal: React.FC<CreateScanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedApplicationId,
}) => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const api = useAuthenticatedApi();
  const { quotaBalance } = useQuotaBalance();
  const { accessType } = useSubscriptionAccess();

  // The modal is always opened from a repository context, so the application is fixed.
  const applicationId = preselectedApplicationId;
  const [application, setApplication] = useState<ApplicationResponse | null>(null);
  const [selectedScanType, setSelectedScanType] = useState<ScanType>('deep_ai_scan');
  const [selectedBranches, setSelectedBranches] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const [quotaModalType, setQuotaModalType] = useState<QuotaType>('deep_scans');

  // Fetch the single application this scan targets when the modal opens.
  useEffect(() => {
    if (!isOpen || !currentOrganization?.id || !applicationId) return;
    let cancelled = false;
    const fetchApp = async () => {
      setLoadError(null);
      try {
        const data = await api.get<ApplicationResponse>(
          apiEndpoints.applications.get(currentOrganization.id, applicationId)
        );
        if (!cancelled) setApplication(data);
      } catch (err) {
        console.error('Failed to fetch application:', err);
        if (!cancelled) setLoadError('Failed to load repository');
      }
    };
    fetchApp();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentOrganization?.id, applicationId, api]);

  // Reset transient state when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setSelectedScanType('deep_ai_scan');
      setSelectedBranches({});
      setApplication(null);
      setLoadError(null);
    }
  }, [isOpen]);

  // Seed branch selections from each repository's default branch.
  useEffect(() => {
    if (!isOpen || !application) {
      setSelectedBranches({});
      return;
    }

    const nextBranches: Record<string, string> = {};
    application.repositories.forEach((repo) => {
      nextBranches[getRepositoryKey(repo.provider, repo.full_name)] = repo.default_branch || 'main';
    });
    setSelectedBranches(nextBranches);
  }, [isOpen, application]);

  if (!isOpen) return null;

  const repositories = application?.repositories ?? [];
  const singleRepo = repositories.length === 1 ? repositories[0] : null;
  const selectedScanTypeName = SCAN_TYPES.find((st) => st.id === selectedScanType)?.name.toLowerCase();

  // Remaining quota for a scan type; null when it isn't metered or is unlimited
  const getQuotaRemaining = (scanType: ScanType): number | null => {
    if (!quotaBalance) return null;
    if (scanType === 'secrets_scan' || scanType === 'semgrep_scan') {
      return isUnlimitedQuota(quotaBalance.plan_sast_scans) ? null : quotaBalance.remaining_sast_scans;
    }
    if (scanType === 'deep_ai_scan') {
      return isUnlimitedQuota(quotaBalance.plan_deep_scans) ? null : quotaBalance.remaining_deep_scans;
    }
    return null;
  };

  const selectedQuotaRemaining = getQuotaRemaining(selectedScanType);
  const isQuotaExhausted = selectedQuotaRemaining !== null && selectedQuotaRemaining <= 0;

  const handleSubmit = async () => {
    if (!applicationId || !application) {
      toast.error('Repository could not be loaded');
      return;
    }

    const branchOverrides = application.repositories.map((repo) => {
      const branch = (selectedBranches[getRepositoryKey(repo.provider, repo.full_name)] || repo.default_branch || 'main').trim();
      return {
        provider: repo.provider || 'github',
        repository_full_name: repo.full_name,
        branch,
      };
    });

    const missingBranch = branchOverrides.find((override) => !override.branch);
    if (missingBranch) {
      toast.error(`Please select a branch for ${missingBranch.repository_full_name}`);
      return;
    }

    setIsSubmitting(true);

    try {
      PostHogService.track(FeatureDepthEvent.SCAN_START_CLICKED, {
        organization_id: currentOrganization?.id,
        organization_role: currentOrganization?.current_user_role,
        access_type: accessType,
        application_id: applicationId,
        scan_type: selectedScanType,
        repository_count: application.repositories.length,
        repo_providers: Array.from(new Set(application.repositories.map((repo) => repo.provider || 'github'))).sort(),
        source: 'manual',
      });

      const requestData: ScanCreateRequest = {
        application_id: applicationId,
        scan_type: selectedScanType,
        source: 'manual',
        branch_overrides: branchOverrides,
      };

      const response = await api.post(apiEndpoints.scans.create(), requestData);
      toast.success(response.message || 'Scan started successfully');
      onClose();

      // Stay in the scans list, which now shows the new scan's progress inline.
      // The list refreshes immediately via the newBatchId location state.
      navigate(`/repositories/${applicationId}/scans`, {
        state: { newBatchId: response.batch_id, timestamp: Date.now() },
      });

      if (onSuccess) {
        onSuccess(response.batch_id);
      }
    } catch (error: any) {
      console.error('Error creating scan:', error);
      const errorQuotaType = error.data?.detail?.quota_type;
      if (errorQuotaType) {
        setQuotaModalType(errorQuotaType as QuotaType);
        setQuotaModalOpen(true);
      } else {
        toast.error(error.message || 'Failed to create scan');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6">
      <div className="bg-ui-background-primary border border-ui-border-primary rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-7">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[19px] font-bold text-ui-text-primary">New Scan</h2>
          <button
            onClick={onClose}
            className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-5">
          {/* Repository context */}
          {application ? (
            <div className="space-y-2">
              {repositories.map((repo) => {
                const repoKey = getRepositoryKey(repo.provider, repo.full_name);
                const provider = repo.provider || 'github';
                const shortName = repo.full_name.split('/').pop() || repo.full_name;
                return (
                  <div
                    key={repoKey}
                    className="flex flex-wrap items-center gap-x-2.5 gap-y-2 bg-ui-background-tertiary border border-ui-border-secondary rounded-lg px-3.5 py-2.5"
                  >
                    <FolderGit2 className="w-4 h-4 text-brand-primary shrink-0" />
                    <span
                      className="font-mono text-sm text-ui-text-primary truncate max-w-[16rem]"
                      title={repo.full_name}
                    >
                      {shortName}
                    </span>
                    {provider !== 'github' && (
                      <span className="text-[11px] uppercase text-ui-text-tertiary bg-ui-background-primary rounded px-1.5 py-0.5 shrink-0">
                        {providerLabel(provider)}
                      </span>
                    )}
                    <span className="text-ui-text-tertiary" aria-hidden>
                      ·
                    </span>
                    {currentOrganization?.id && (
                      <ScanBranchSelector
                        compact
                        organizationId={currentOrganization.id}
                        repo={repo}
                        value={selectedBranches[repoKey] || repo.default_branch || 'main'}
                        onChange={(branch) => {
                          setSelectedBranches((current) => ({
                            ...current,
                            [repoKey]: branch,
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : loadError || !applicationId ? (
            <div className="text-center py-6 border border-ui-border-secondary rounded-lg">
              <p className="text-sm text-ui-text-secondary">{loadError || 'Repository not found'}</p>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4 border border-ui-border-secondary rounded-lg">
              <Spinner size={20} />
            </div>
          )}

          {/* Scan Type Selection */}
          <div>
            <label className="block text-xs font-semibold text-ui-text-secondary mb-2.5">
              Scan type
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SCAN_TYPES.map((scanType) => (
                <button
                  key={scanType.id}
                  onClick={() => setSelectedScanType(scanType.id)}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    selectedScanType === scanType.id
                      ? 'border-brand-primary bg-brand-primary bg-opacity-10'
                      : 'border-ui-border-secondary hover:border-ui-border-primary'
                  }`}
                  disabled={isSubmitting}
                >
                  <div className="flex items-start gap-3">
                    <div className={`${selectedScanType === scanType.id ? 'text-brand-primary' : 'text-ui-text-secondary'}`}>
                      {scanType.icon}
                    </div>
                    <div className="flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-ui-text-primary">{scanType.name}</h3>
                        {scanType.recommended && (
                          <span className="rounded-full bg-brand-primary/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-brand-primary">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ui-text-secondary mb-2">{scanType.description}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-ui-text-tertiary">{scanType.duration}</p>
                        {quotaBalance && (() => {
                          const remaining = getQuotaRemaining(scanType.id);
                          if (remaining === null) return null;
                          return (
                            <span className={`text-xs font-medium ${remaining <= 0 ? 'text-red-500' : remaining <= 2 ? 'text-amber-500' : 'text-ui-text-tertiary'}`}>
                              {remaining <= 0 ? 'No quota remaining' : `${remaining} remaining`}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {application && singleRepo ? (
            <div className="bg-ui-background-tertiary rounded-lg px-4 py-3">
              <p className="text-[13px] text-ui-text-secondary">
                This will scan{' '}
                <span className="font-semibold text-ui-text-primary">
                  {singleRepo.full_name.split('/').pop() || singleRepo.full_name}
                </span>{' '}
                on{' '}
                <span className="font-semibold text-ui-text-primary">
                  {selectedBranches[getRepositoryKey(singleRepo.provider, singleRepo.full_name)] || singleRepo.default_branch || 'main'}
                </span>{' '}
                with a {selectedScanTypeName}.
              </p>
            </div>
          ) : application ? (
            <div className="bg-ui-background-tertiary rounded-lg px-4 py-3">
              <p className="text-[13px] text-ui-text-secondary">
                This will scan <span className="font-semibold text-ui-text-primary">{repositories.length}</span>{' '}
                {repositories.length === 1 ? 'repository' : 'repositories'} in{' '}
                <span className="font-semibold text-ui-text-primary">{application.name}</span> for{' '}
                {selectedScanTypeName}.
              </p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-ui-text-secondary hover:text-ui-text-primary transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !application || isQuotaExhausted}
            className="px-5 py-2.5 bg-brand-primary hover:bg-brand-primaryDark text-black font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting && <Spinner size={16} tone="current" />}
            {isQuotaExhausted ? 'Quota Exhausted' : isSubmitting ? 'Starting Scan...' : 'Start Scan'}
          </button>
        </div>
      </div>

      <QuotaExceededModal
        isOpen={quotaModalOpen}
        onClose={() => setQuotaModalOpen(false)}
        quotaType={quotaModalType}
        isFreeTier={accessType === 'free'}
      />
    </div>
  );
};

export default CreateScanModal;
